import React, { useEffect, useState } from "react";
import DrawerLayout from "../..";
import { ProfileIcon } from "../../../../icons";
import Input from "../../../form/input";
import Select from "../../../select";
import Switch from "../../../switch";
import "./style.scss";
import { useUpdateMember } from "../../../../hooks/useUpdateMember";
import { useDepots } from "../../../../hooks/useDepots";
import { useUnitGroups } from "../../../../api/unitGroups";
import { useRoles } from "../../../../hooks/useRoles";
import { message } from "antd";
import { isPermit } from "../../../../utils/permissionCheck";

interface AddUserProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  title?: string;
  user?: any;
  mode: "add" | "edit" | "view";
  onEditClick?: () => void;
}

export type User = {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  depot: number[];
  unitGroup: number[];
  memberType: number | "";
  role: number | "";
  active: boolean;
};

// Map API → Form data
const mapApiUserToFormData = (apiUser: any): User => ({
  id: Number(apiUser.pikitup_member_id),
  firstName: apiUser.member_first_name,
  lastName: apiUser.member_surname,
  email: apiUser.member_email_id || "",
  mobile: apiUser.member_mobile_no?.toString() || "",
  depot: Array.isArray(apiUser.depot_ids)
    ? apiUser.depot_ids.map((d: any) => Number(d.pikitup_depot_id))
    : apiUser.pikitup_depot_id
    ? [Number(apiUser.pikitup_depot_id)]
    : [],
  unitGroup:
    apiUser.unit_groups?.map((ug: any) => Number(ug.pikitup_unit_group_id)) ||
    [],
  memberType: Number(apiUser.member_type) || "",
  role: Number(apiUser.role_id) || "",
  active: apiUser.profile_status_flg === 1,
});

// Map Form → API Payload
const mapUserToMemberPayload = (user: User, mode: "add" | "edit"): any => ({
  action: mode === "add" ? "create" : "update",
  member_id: mode === "edit" ? user.id : null,
  depot_ids: user.depot,
  member_first_name: user.firstName,
  member_surname: user.lastName,
  member_type: Number(user.memberType),
  preffered_comm_channel: "Email",
  member_country_code: "27",
  member_mobile_no: Number(user.mobile),
  member_email_id: user.email || null,
  profile_status_flg: user.active ? 1 : 0,
  unit_group_ids: user.unitGroup,
  role_id: Number(user.role),
});

const AddUser: React.FC<AddUserProps> = ({
  open,
  setOpen,
  title = "Add User",
  user,
  mode,
  onEditClick,
}) => {
  const [formData, setFormData] = useState<User>({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    depot: [],
    unitGroup: [],
    memberType: "",
    role: "",
    active: true,
  });

  const [isPrefilled, setIsPrefilled] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  // Prefill form on edit
  useEffect(() => {
    if (mode === "edit" && user) {
      setFormData(mapApiUserToFormData(user));
      setIsPrefilled(true);
    } else {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        depot: [],
        unitGroup: [],
        memberType: "",
        role: "",
        active: true,
      });
    }
  }, [mode, user, open]);

  // --- Fetch depots ---
  const { data: depots } = useDepots();

  let depotOptions =
    depots?.map((d) => ({
      label: d.depot_name?.trim(),
      value: Number(d.pikitup_depot_id),
    })) || [];

  if (mode === "edit" && user?.depot_ids) {
    const fallbackDepots = user.depot_ids.map((d: any) => ({
      label: d.depot_name?.trim(),
      value: Number(d.pikitup_depot_id),
    }));

    const combined = [...depotOptions, ...fallbackDepots];

    const uniqueByValue = new Map<number, { label: string; value: number }>();
    for (const d of combined) {
      const val = Number(d.value);
      if (!uniqueByValue.has(val)) uniqueByValue.set(val, d);
    }

    depotOptions = Array.from(uniqueByValue.values());
  }

  depotOptions.sort((a, b) => a.label.localeCompare(b.label));

  // Fetch unit groups
  const { data: unitGroupsData, refetch: refetchUnitGroups } = useUnitGroups(
    {
      status: 1,
      depot_ids:
        formData.depot.length > 0 ? formData.depot.map(Number) : undefined,
    },
    false
  );

  // Refetch when depot changes (avoid clearing prefilled data)
  useEffect(() => {
    if (formData.depot.length > 0) {
      refetchUnitGroups();
      if (mode === "add") {
        setFormData((prev) => ({ ...prev, unitGroup: [] }));
      } else if (!isPrefilled) {
        setFormData((prev) => ({ ...prev, unitGroup: [] }));
      } else {
        setIsPrefilled(false);
      }
    }
  }, [formData.depot]);

  const [unitGroupSearch, setUnitGroupSearch] = useState("");

  const allUnitGroups =
    unitGroupsData?.data?.unitGroups?.map((ug: any) => ({
      label: ug.unit_group_name,
      value: ug.pikitup_unit_group_id,
    })) || [];

  let filteredUnitGroups = allUnitGroups.filter((ug) =>
    ug.label.toLowerCase().includes(unitGroupSearch.toLowerCase())
  );

  // Add fallback unit groups from user if missing (for edit mode)
  if (mode === "edit" && user?.unit_groups) {
    const fallbackUGs = user.unit_groups.map((ug: any) => ({
      label: ug.unit_group_name,
      value: Number(ug.pikitup_unit_group_id),
    }));
    fallbackUGs.forEach((ug) => {
      if (!filteredUnitGroups.find((opt) => opt.value === ug.value)) {
        filteredUnitGroups.push(ug);
      }
    });
  }

  const { mutate, isPending } = useUpdateMember();
  const memberTypeOptions = [
    { label: "Ops Manager", value: 1 },
    { label: "Business Consultant", value: 2 },
    { label: "CSO", value: 3 },
    { label: "Supervisor", value: 4 },
    { label: "Team Leader", value: 5 },
    { label: "Driver", value: 6 },
    { label: "Field Agent", value: 7 },
    { label: "Regional Manager", value: 8 },
    { label: "Super Admin", value: 9 },
    { label: "General Manager", value: 10 },
  ];

  // Only GM (10) and RM (8) can select multiple depots
  const MULTI_DEPOT_MEMBER_TYPES = [8, 10, 9];
  const allowMultipleDepots = MULTI_DEPOT_MEMBER_TYPES.includes(
    Number(formData.memberType)
  );

  // Roles data
  const { data: rolesData } = useRoles({
    page: 1,
    perPage: 50,
    paginate: false,
  });

  const roleOptions =
    rolesData?.roles?.map((role) => ({
      label: role.role_name,
      value: Number(role.role_id),
    })) || [];

  console.log(roleOptions, "roleOptions");

  // ✅ Add fallback role if not in API
  if (mode === "edit" && user?.role_id && user?.role_name) {
    const exists = roleOptions.some((r) => r.value === Number(user.role_id));
    if (!exists) {
      roleOptions.push({
        label: user.role_name,
        value: Number(user.role_id),
      });
    }
  }

  const handleConfirm = () => {
    if (mode === "add" || mode === "edit") {
      if (!formData.firstName.trim()) {
        messageApi.warning("First name is required");
        return;
      }
      if (formData.firstName.trim().length < 3) {
        messageApi.warning("First name must be at least 3 characters");
        return;
      }

      if (!formData.lastName.trim()) {
        messageApi.warning("Last name is required");
        return;
      }
      if (formData.lastName.trim().length < 3) {
        messageApi.warning("Last name must be at least 3 characters");
        return;
      }

      // ✅ Validate email
      if (!formData.email.trim()) {
        messageApi.warning("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        messageApi.warning("Please enter a valid email address");
        return;
      }

      // ✅ Validate mobile number
      if (!formData.mobile.trim()) {
        messageApi.warning("Mobile number is required");
        return;
      }
      if (!/^\d{10}$/.test(formData.mobile.trim())) {
        messageApi.warning("Mobile number must be exactly 10 digits");
        return;
      }

      if (formData.depot.length === 0) {
        messageApi.warning("Please select at least one depot");
        return;
      }

      if (!formData.memberType) {
        messageApi.warning("Member type is required");
        return;
      }

      if (!formData.role) {
        messageApi.warning("Role is required");
        return;
      }

      const payload = mapUserToMemberPayload(formData, mode);

      mutate(payload, {
        onSuccess: () => {
          messageApi.success(
            mode === "add"
              ? "User invited successfully!"
              : "User updated successfully!"
          );
          setOpen(false);
        },
        onError: (error: any) => {
          const statusCode = error?.response?.data?.statusCode;
          let msg =
            error?.response?.data?.message ||
            error?.message ||
            "Something went wrong!";
          if (statusCode === 409)
            msg = "User already exists with this mobile number!";
          messageApi.open({
            type: "error",
            content: msg,
          });
        },
      });
    }
  };

  const handleConfirmClick = () => {
    if (mode === "view") onEditClick?.();
    else handleConfirm();
  };

  const labelMappings = [
    { label: "First name", value: user?.member_first_name },
    { label: "Last name", value: user?.member_surname },
    {
      label: "Mobile number",
      value: `${user?.member_country_code || ""} ${
        user?.member_mobile_no || "-"
      }`,
    },
    { label: "Email", value: user?.member_email_id },
    {
      label: "Member type",
      value:
        user?.member_type_name ||
        memberTypeOptions.find((m) => m.value === String(user?.member_type))
          ?.label,
    },
    { label: "Created on", value: user?.created_date },
  ];

  const StatusPill: React.FC<{ status: number }> = ({ status }) => {
    const label =
      status === 1 ? "Active" : status === 0 ? "Inactive" : "Deleted";
    return (
      <span className={`status-pill status-${label.toLowerCase()}`}>
        {label}
      </span>
    );
  };

  const showUnitGroup = [4, 5, 6].includes(Number(formData.memberType));

  return (
    <div className="test">
      {contextHolder}
      <DrawerLayout
        extra={<ProfileIcon />}
        open={open}
        setOpen={setOpen}
        title={title}
        onConfirm={handleConfirmClick}
        confirmText={
          mode === "add"
            ? "Send invitation"
            : mode === "view"
            ? "Edit User"
            : "Update Member"
        }
        cancelText="Cancel"
        footer={
          (isPermit(3, "create") && mode === "add") || isPermit(3, "edit")
        }
        loading={isPending}
      >
        {mode === "view" ? (
          <>
            <div className="user-details-view">
              {labelMappings.map((item, index) => (
                <p className="label" key={index}>
                  {item.label}{" "}
                  <span className="value">{item.value || "-"}</span>
                </p>
              ))}

              {/* Depot list as bullets */}
              <div className="label">
                Depot names
                <ul
                  className="value"
                  style={{ marginTop: "6px", paddingLeft: "18px" }}
                >
                  {Array.isArray(user?.depot_ids) &&
                  user.depot_ids.length > 0 ? (
                    user.depot_ids.map((d: any) => (
                      <li key={d.pikitup_depot_id}>{d.depot_name}</li>
                    ))
                  ) : (
                    <li>-</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1 status-wrapper">
                <span className="label mb-0">Status</span>
                <StatusPill status={user?.profile_status_flg ?? -1} />
              </div>
              <div className="flex flex-col roll-wrapper">
                <span className="label mb-1">System User Role</span>
                <span>{user?.role_name}</span>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-6 p-4">
            <div className="grid grid-cols-2 gap-8">
              <Input
                label="First name"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
              />
              <Input
                label="Last name"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-8">
              <Input
                label="Email"
                placeholder="Enter email ID"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
              <Input
                type="number"
                maxLength={10}
                label="Mobile number"
                placeholder="Enter mobile number"
                value={formData.mobile}
                onChange={(e) =>
                  setFormData({ ...formData, mobile: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-4 mt-3">
              <Select
                label="Member type"
                options={memberTypeOptions}
                placeholder="Choose a member type"
                value={formData.memberType}
                onChange={(value: number) => {
                  setFormData({
                    ...formData,
                    memberType: value,
                    depot: [],
                    unitGroup: [],
                  });
                }}
              />

              <Select
                allowClear={false}
                label="Depot"
                options={depotOptions}
                placeholder={
                  allowMultipleDepots
                    ? "Choose depot(s)"
                    : "Choose a single depot"
                }
                value={formData.depot}
                mode={allowMultipleDepots ? "multiple" : undefined}
                onChange={(value: number[] | number) => {
                  // If multi allowed, store as array. If not, only store last selected depot.
                  const selectedValue = Array.isArray(value)
                    ? allowMultipleDepots
                      ? value
                      : [value[value.length - 1]]
                    : [value];
                  setFormData({ ...formData, depot: selectedValue });
                }}
              />

              {showUnitGroup && (
                <Select
                  allowClear={false}
                  label="Unit groups"
                  placeholder="Choose unit groups"
                  value={formData.unitGroup}
                  options={filteredUnitGroups}
                  mode="multiple"
                  showSearch
                  onSearch={(val) => setUnitGroupSearch(val)}
                  onChange={(value: number[]) =>
                    setFormData({ ...formData, unitGroup: value })
                  }
                />
              )}
              <Select
                label="System User Role"
                options={roleOptions}
                placeholder="Choose a role"
                value={formData.role}
                onChange={(value: number) =>
                  setFormData({ ...formData, role: value })
                }
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="font-medium text-gray-700">Active</label>
              <Switch
                size="default"
                checked={formData.active}
                onChange={(checked: boolean) =>
                  setFormData({ ...formData, active: checked })
                }
              />
            </div>
          </div>
        )}
      </DrawerLayout>
    </div>
  );
};

export default AddUser;
